import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import IOrder_ItemUI from "@models/IOrder_ItemUI";
import React, { useState } from "react";
import { OrderEntryEditSerial } from "../OrderEntryEditSerial/OrderEntryEditSerial";
import ISerial from "@models/ISerial";
import { useAppContextNonNull } from "@contexts/AppContext";
import IOrderEntryProcessSerialDTO from "@models/DTO/IOrderEntryProcessSerialDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import IProcessSerialDTO from "@models/DTO/IProcessSerialDTO";
import OwButton from "@components/shared/OwButton/OwButton";
import { BulkAddSerialSidebar } from "../BulkAddSerialSidebar/BulkAddSerialSidebar";
import "./OrderEntrySerialDetail.scss";
import FastBoundSerialStatusEnum from "@models/FastBoundSerialStatusEnum";
import IOrder from "@models/IOrder";

type Props = {
    order: IOrder;
    row: IOrder_ItemUI;
    isReadOnly: boolean;
    handleRowSerial: (e: any, row: IOrder_ItemUI) => void;
    refreshOrder: () => void;
};

interface ISerialUI extends ISerial {
    editMode: boolean;
    confirmDelete: boolean;
}

export const OrderEntrySerialDetail = (props: Props) => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();

    const [availableSerials, setAvailableSerials] = useState<any[]>([]);
    const [resetTypeahead, setResetTypeahead] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const lookupSerials = async (value: string) => {
        if (!value) {
            setAvailableSerials([]);
            return;
        }
        const query = {
            serial: value,
            unassigned: "true",
            fastbound_status: "active,null",
            fastbound_account_id: props.order.Vendor?.Vendor_Integration?.fastbound_account_id,
        };
        const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.SerialsGetAll, query });
        const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
        const response = await fetch(url, { method: "GET", headers });
        if (response.ok) {
            const data = (await response.json()) as { serials: ISerial[]; totalCount: number };
            const serials = data.serials || [];
            const hasExactMatch = serials.some((s: ISerial) => s.value === value);
            const options: any[] = [...serials];
            if (!hasExactMatch && props.row.Catalog_Item?.manual_serial_entry) {
                options.unshift({ value, _isNew: true });
            }
            setAvailableSerials(options);
        }
    };

    const handleSerialSelected = (option: ISerial) => {
        const payload: IOrderEntryProcessSerialDTO = {
            supplierGuid: supplierModel.guid,
            orderItemGuid: props.row.guid,
            userToken: userModel.token,
            userGuid: userModel.guid,
        };

        if (option.id) {
            payload.serialId = option.id;
        } else {
            payload.serial = option.value;
        }

        socket.emit(EndpointsEnum.OrderEntryProcessSerial, payload);
        setResetTypeahead(true);
        setAvailableSerials([]);
    };

    const updateSerial = (payload: IProcessSerialDTO) => {
        // The Order Entry consumes the OnProcessSerial socket Endpoint response
        socket.emit(EndpointsEnum.OrderEntryProcessSerial, payload);
    };

    const openBulkAddSidebar = () => {
        const cancelBulkAdd = (refresh?: boolean) => {
            uiContext.setSidebarComponent(null);
            uiContext.toggleSidebar(false);
            if (refresh) {
                props.refreshOrder();
            }
        };

        uiContext.setSidebarComponent(<BulkAddSerialSidebar row={props.row} cancel={cancelBulkAdd} order={props.order} />);
        uiContext.toggleSidebar(true);
    };

    // if (props.row.Catalog_Item?.manual_serial_entry) {
    // if (row.rowEditMode) {
    return (
        <div className="OrderEntrySerialDetail">
            {props.isReadOnly || (props.row.Serials?.length || 0) >= (props.row.qty || 0) ? null : (
                <div className="d-flex gap-2">
                    <div className={"col-3 .g-0"}>
                        <TypeaheadFn
                            lookupFn={lookupSerials}
                            availableOptions={availableSerials}
                            selectedOptionFn={handleSerialSelected}
                            titleProp={"value"}
                            renderFn={(item: any) => (
                                <div>
                                    {item._isNew ? (
                                        <span>
                                            <i className="fas fa-plus me-1" />
                                            Create: {item.value}
                                        </span>
                                    ) : (
                                        <span>{item.value}</span>
                                    )}
                                </div>
                            )}
                            placeholder={props.row.Catalog_Item?.manual_serial_entry ? "search_or_create_serial" : "search_serial"}
                            confirmAdd={true}
                            usePortal={true}
                            resetTypeahead={resetTypeahead}
                            resetTypeaheadCallbackFn={() => setResetTypeahead(false)}
                            disabled={
                                !!(
                                    props.row.Catalog_Item?.max_serials_per_order_item &&
                                    (props.row.Serials || []).length >= props.row.Catalog_Item?.max_serials_per_order_item
                                )
                            }
                        />
                    </div>
                    <div className="col-1">
                        <OwButton
                            title="bulk_add"
                            fn={openBulkAddSidebar}
                            disabled={
                                !!(
                                    props.row.Catalog_Item?.max_serials_per_order_item &&
                                    (props.row.Serials || []).length >= props.row.Catalog_Item?.max_serials_per_order_item
                                )
                            }
                        />
                    </div>
                </div>
            )}

            <div className={"mt-4"}>
                <div className="serial-toggle-pill " onClick={() => setIsExpanded(!isExpanded)}>
                    <div className="serial-toggle-chevron bg-primary">
                        <i className={isExpanded ? "fas fa-chevron-down" : "fas fa-chevron-right"} />
                    </div>
                    <div className="serial-toggle-label">
                        <OwLabel title={"serials"} />
                    </div>
                </div>

                {isExpanded ? (
                    ((props.row.Serials || []) as ISerialUI[]).map((serial) => {
                        return (
                            <OrderEntryEditSerial
                                key={serial.guid}
                                serial={serial}
                                row={props.row}
                                saveFn={updateSerial}
                                readOnly={props.isReadOnly || serial.fastbound_status === FastBoundSerialStatusEnum.DISPOSED}
                            />
                        );
                    })
                ) : (
                    <>
                        {((props.row.Serials || []) as ISerialUI[]).slice(0, 2).map((serial) => (
                            <div key={serial.id} className="mb-1">
                                {serial.value}
                            </div>
                        ))}
                        {(props.row.Serials || []).length > 2 && (
                            <div className="text-muted small">+{(props.row.Serials || []).length - 2} more</div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
    // } else {
    //     return (
    //         <div>
    //             <b>
    //                 <OwLabel title={"serials"} />:
    //             </b>{" "}
    //             {(row.Serials || []).map((serial) => { return <div>{serial.value}</div> })}
    //         </div>
    //     );
    // }
    // }
    // return <div className={"OrderEntrySerialDetail"}></div>;
};
